// lib/blockService.js
// Block/unblock service — synced with mobile (src/lib/blockService.ts)
// Firestore: users/{mavenId}/blockedSeekers/{seekerId}

import { db } from './firebase';
import {
  collection,
  doc,
  getDoc,
  getDocs,
  query,
  orderBy,
  runTransaction,
  serverTimestamp,
} from 'firebase/firestore';

const toSafeCount = (value) => {
  const parsed = Number(value);
  if (!Number.isFinite(parsed) || parsed < 0) return 0;
  return Math.floor(parsed);
};

/** Check if a seeker is blocked by a maven (single doc read) */
export async function isBlockedByMaven(mavenId, seekerId) {
  if (!mavenId || !seekerId) return false;
  const snap = await getDoc(doc(db, 'users', mavenId, 'blockedSeekers', seekerId));
  return snap.exists();
}

/** Get all blocked seekers for a maven (for management page) */
export async function getBlockedSeekerIds(mavenId) {
  if (!mavenId) return [];
  const snap = await getDocs(
    query(
      collection(db, 'users', mavenId, 'blockedSeekers'),
      orderBy('blockedAt', 'desc')
    )
  );
  return snap.docs.map((d) => ({ id: d.id, ...d.data() }));
}

/** Toggle block — block or unblock. Returns new state. */
export async function toggleBlockSeeker({ mavenId, seekerId, seekerName, seekerPhotoURL }) {
  if (!mavenId || !seekerId) {
    throw new Error('Missing mavenId or seekerId');
  }

  return runTransaction(db, async (transaction) => {
    const blockRef = doc(db, 'users', mavenId, 'blockedSeekers', seekerId);
    const expertRef = doc(db, 'experts', mavenId);
    const mavenUserRef = doc(db, 'users', mavenId);

    const [blockSnap, expertSnap, mavenUserSnap] = await Promise.all([
      transaction.get(blockRef),
      transaction.get(expertRef),
      transaction.get(mavenUserRef),
    ]);

    const expertData = expertSnap.exists() ? expertSnap.data() : {};
    const mavenUserData = mavenUserSnap.exists() ? mavenUserSnap.data() : {};

    const currentCount = Math.max(
      toSafeCount(expertData.blockedSeekersCount),
      toSafeCount(mavenUserData.blockedSeekersCount)
    );

    const isAlreadyBlocked = blockSnap.exists();
    const nextCount = isAlreadyBlocked ? Math.max(0, currentCount - 1) : currentCount + 1;

    if (isAlreadyBlocked) {
      transaction.delete(blockRef);
    } else {
      transaction.set(blockRef, {
        seekerId,
        seekerName: seekerName || 'Seeker',
        seekerPhotoURL: seekerPhotoURL || '',
        blockedAt: serverTimestamp(),
      });
    }

    transaction.set(
      expertRef,
      { blockedSeekersCount: nextCount, updatedAt: serverTimestamp() },
      { merge: true }
    );

    transaction.set(
      mavenUserRef,
      { blockedSeekersCount: nextCount, updatedAt: serverTimestamp() },
      { merge: true }
    );

    return { isBlocked: !isAlreadyBlocked, blockedCount: nextCount };
  });
}

/** Block a seeker (idempotent — no-op if already blocked) */
export async function blockSeeker({ mavenId, seekerId, seekerName, seekerPhotoURL }) {
  if (!mavenId || !seekerId) {
    throw new Error('Missing mavenId or seekerId');
  }

  return runTransaction(db, async (transaction) => {
    const blockRef = doc(db, 'users', mavenId, 'blockedSeekers', seekerId);
    const expertRef = doc(db, 'experts', mavenId);
    const mavenUserRef = doc(db, 'users', mavenId);

    const [blockSnap, expertSnap, mavenUserSnap] = await Promise.all([
      transaction.get(blockRef),
      transaction.get(expertRef),
      transaction.get(mavenUserRef),
    ]);

    if (blockSnap.exists()) {
      const expertData = expertSnap.exists() ? expertSnap.data() : {};
      const mavenUserData = mavenUserSnap.exists() ? mavenUserSnap.data() : {};
      const currentCount = Math.max(
        toSafeCount(expertData.blockedSeekersCount),
        toSafeCount(mavenUserData.blockedSeekersCount)
      );
      return { isBlocked: true, blockedCount: currentCount };
    }

    const expertData = expertSnap.exists() ? expertSnap.data() : {};
    const mavenUserData = mavenUserSnap.exists() ? mavenUserSnap.data() : {};
    const currentCount = Math.max(
      toSafeCount(expertData.blockedSeekersCount),
      toSafeCount(mavenUserData.blockedSeekersCount)
    );
    const nextCount = currentCount + 1;

    transaction.set(blockRef, {
      seekerId,
      seekerName: seekerName || 'Seeker',
      seekerPhotoURL: seekerPhotoURL || '',
      blockedAt: serverTimestamp(),
    });

    transaction.set(
      expertRef,
      { blockedSeekersCount: nextCount, updatedAt: serverTimestamp() },
      { merge: true }
    );

    transaction.set(
      mavenUserRef,
      { blockedSeekersCount: nextCount, updatedAt: serverTimestamp() },
      { merge: true }
    );

    return { isBlocked: true, blockedCount: nextCount };
  });
}

/** Unblock a seeker (idempotent — no-op if not blocked) */
export async function unblockSeeker({ mavenId, seekerId }) {
  if (!mavenId || !seekerId) {
    throw new Error('Missing mavenId or seekerId');
  }

  return runTransaction(db, async (transaction) => {
    const blockRef = doc(db, 'users', mavenId, 'blockedSeekers', seekerId);
    const expertRef = doc(db, 'experts', mavenId);
    const mavenUserRef = doc(db, 'users', mavenId);

    const [blockSnap, expertSnap, mavenUserSnap] = await Promise.all([
      transaction.get(blockRef),
      transaction.get(expertRef),
      transaction.get(mavenUserRef),
    ]);

    if (!blockSnap.exists()) {
      const expertData = expertSnap.exists() ? expertSnap.data() : {};
      const mavenUserData = mavenUserSnap.exists() ? mavenUserSnap.data() : {};
      const currentCount = Math.max(
        toSafeCount(expertData.blockedSeekersCount),
        toSafeCount(mavenUserData.blockedSeekersCount)
      );
      return { isBlocked: false, blockedCount: currentCount };
    }

    const expertData = expertSnap.exists() ? expertSnap.data() : {};
    const mavenUserData = mavenUserSnap.exists() ? mavenUserSnap.data() : {};
    const currentCount = Math.max(
      toSafeCount(expertData.blockedSeekersCount),
      toSafeCount(mavenUserData.blockedSeekersCount)
    );
    const nextCount = Math.max(0, currentCount - 1);

    transaction.delete(blockRef);

    transaction.set(
      expertRef,
      { blockedSeekersCount: nextCount, updatedAt: serverTimestamp() },
      { merge: true }
    );

    transaction.set(
      mavenUserRef,
      { blockedSeekersCount: nextCount, updatedAt: serverTimestamp() },
      { merge: true }
    );

    return { isBlocked: false, blockedCount: nextCount };
  });
}
